import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IStockType, IStockTypeInput, stockTypesAtom, stockTypesLoadingAtom } from "./types";

type UseStockTypes = [IStockType[], {
    list: (query?: Record<string, any>) => Promise<IStockType[] | null>;
    getById: (id: number) => Promise<IStockType | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IStockType[] | null>;
    create: (input: IStockTypeInput) => Promise<IStockType | null>;
    update: (id: number, input: IStockTypeInput) => Promise<IStockType | null>;
    remove: (id: number) => Promise<IStockType | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useStockTypes = (): UseStockTypes => {
    const [items, setItems] = useAtom(stockTypesAtom);
    const setLoading = useSetAtom(stockTypesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getStockTypes(query: $query){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getStockTypes);
            return data.getStockTypes as IStockType[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch stock types.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getStockTypeById(id:$id){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getStockTypeById as IStockType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedStockTypes(first:$first,offset:$offset,query:$query){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedStockTypes as IStockType[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IStockTypeInput) => {
        try { const q = `mutation($input:StockTypeInput!){ createStockType(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createStockType as IStockType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IStockTypeInput) => {
        try { const q = `mutation($id:ID!,$input:StockTypeInput!){ updateStockType(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateStockType as IStockType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteStockType(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteStockType as IStockType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


